import { useMemo } from "react";
import { RadialBar, RadialBarChart as RechartsRadialBarChart } from "recharts";

import { ChartLegend, ChartLegendContent } from "@/components/ui/ChartLegend";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColorCssVarKey,
} from "@/components/ui/ChartTooltip";
import { cn } from "@/lib/utils";

import type { RadialBarDatum } from "./radialBarChartUtils";

export interface RadialBarChartProps {
  data: RadialBarDatum[];
  chartConfig: ChartConfig;
  /** The numeric field to chart (typically `"value"`). */
  dataKey: string;
  /** Field used for bar identity; values should match keys in `chartConfig` for theme colors. */
  nameKey: string;
  /** Optional title above the chart (chat typography: `text-xxs font-medium`). */
  title?: string | null;
  /** Fixed height for the chart block (px); omit for max-height–only sizing. */
  height?: number | null;
  /** Renders {@link ChartLegend} + {@link ChartLegendContent} below the chart. @default true */
  showLegend?: boolean;
  /** @default `"25%"` */
  innerRadius?: number | string;
  /** @default `"90%"` */
  outerRadius?: number | string;
  /** Passed to {@link ChartContainer}. */
  className?: string;
  /**
   * Bar enter animation (Recharts). Default on; set false for reduced motion or tests.
   * @default true
   */
  isAnimationActive?: boolean;
  /**
   * Recharts v3: keyboard focus and ARIA on the chart SVG.
   * @default true
   */
  accessibilityLayer?: boolean;
}

const RadialBarChartEmpty = () => (
  <div className="flex w-full min-w-0 items-center justify-center rounded-md border border-dashed border-muted-foreground/25 py-4 text-xxs text-muted-foreground">
    No data to display
  </div>
);

/**
 * Radial bar chart for chat and dashboards: same density tokens as PieDonut
 * (compact `text-xxs`, dashed empty state), default 25%/90% radii, legend,
 * shadcn tooltip, background track, and rounded corners.
 */
const RadialBarChartView = ({
  data,
  chartConfig,
  dataKey,
  nameKey,
  title = null,
  height = null,
  showLegend = true,
  innerRadius = "25%",
  outerRadius = "90%",
  className,
  isAnimationActive = true,
  accessibilityLayer = true,
}: RadialBarChartProps) => {
  /**
   * Stable `data` reference when inputs are unchanged. Recharts compares data
   * by reference; a new array every render resets animation and can make the
   * bars invisible.
   */
  const dataWithFill = useMemo(
    () =>
      data.map((row) => {
        const raw = row[nameKey];
        const key = typeof raw === "string" ? raw : String(raw);
        const fill = row.fill ?? `var(--color-${chartColorCssVarKey(key)})`;
        return { ...row, fill };
      }),
    [data, nameKey],
  );

  const total = dataWithFill.reduce((sum, row) => {
    const v = (row as RadialBarDatum)[dataKey];
    const n = typeof v === "number" ? v : Number(v);
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);

  if (total === 0 || dataWithFill.length === 0) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {title != null && title !== "" ? (
          <p className="m-0 text-xxs font-medium text-foreground">{title}</p>
        ) : null}
        <RadialBarChartEmpty />
      </div>
    );
  }

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-1.5"
      style={height != null ? { minHeight: height, height } : undefined}
    >
      {title != null && title !== "" ? (
        <p className="m-0 text-xxs font-medium text-foreground">{title}</p>
      ) : null}
      <ChartContainer
        config={chartConfig}
        className={cn(
          "mx-auto aspect-square w-full",
          height == null ? "max-h-[240px]" : "max-h-[min(280px,100%)]",
          className,
        )}
      >
        <RechartsRadialBarChart
          data={dataWithFill}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          accessibilityLayer={accessibilityLayer}
        >
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent nameKey={nameKey} />}
          />
          <RadialBar
            dataKey={dataKey}
            background
            cornerRadius={6}
            isAnimationActive={isAnimationActive}
          />
          {showLegend ? (
            <ChartLegend
              verticalAlign="bottom"
              content={
                <ChartLegendContent
                  nameKey={nameKey}
                  className="flex-wrap gap-x-2 gap-y-0.5 pt-1.5 text-foreground dark:text-gray-300"
                />
              }
            />
          ) : null}
        </RechartsRadialBarChart>
      </ChartContainer>
    </div>
  );
};

export default RadialBarChartView;
